import * as React from 'react';

export default class AiDevPanel extends React.Component {
  constructor(props) {
    super(props);
    const options = props.gptApiKeyOptions || [];
    this.state = {
      gptApiKeyId: options.length ? options[0].id.toString() : '',
      code: props.defaultCode || '',
      permissionConfirmed: false,
      running: false,
      error: null,
      result: null
    };

    this.runCode = this.runCode.bind(this);
  }

  runCode(event) {
    event.preventDefault();
    const { runUrl } = this.props;
    const { gptApiKeyId, code, permissionConfirmed } = this.state;

    this.setState({
      running: true,
      error: null,
      result: null
    });

    const token = document.querySelector('meta[name="csrf-token"]');
    const csrfToken = token ? token.getAttribute('content') : '';

    fetch(runUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        gpt_api_key_id: gptApiKeyId,
        code: code,
        permission_confirmed: permissionConfirmed
      })
    })
      .then((response) => response.json().then((payload) => ({ response, payload })))
      .then(({ response, payload }) => {
        if (!response.ok) {
          throw new Error(payload.error || 'Execution failed');
        }

        this.setState({
          running: false,
          result: payload
        });
      })
      .catch((err) => {
        this.setState({
          running: false,
          error: err.message || 'Execution failed'
        });
      });
  }

  render() {
    const { gptApiKeyOptions } = this.props;
    const { gptApiKeyId, code, permissionConfirmed, running, error, result } = this.state;
    const selectedKey = (gptApiKeyOptions || []).find((option) => option.id.toString() === gptApiKeyId.toString());

    const cardStyle = {
      background: '#101418',
      border: '1px solid #27303a',
      borderRadius: '12px',
      padding: '16px',
      color: '#d7e2ee',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.28)'
    };

    const labelStyle = {
      display: 'block',
      marginBottom: '8px',
      fontWeight: 600,
      color: '#eef4fb'
    };

    const inputStyle = {
      width: '100%',
      borderRadius: '10px',
      border: '1px solid #344252',
      background: '#0a0f14',
      color: '#f4f7fb',
      padding: '10px 12px',
      fontFamily: 'inherit'
    };

    const monoStyle = Object.assign({}, inputStyle, {
      fontFamily: 'IBM Plex Mono, SFMono-Regular, Menlo, Monaco, Consolas, monospace'
    });

    return (
      <div style={{ display: 'grid', gap: '16px' }}>
        <div style={cardStyle}>
          <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'minmax(280px, 1fr) minmax(320px, 2fr)' }}>
            <div>
              <label style={labelStyle} htmlFor="ai-dev-gpt-api-key">
                GPT API key
              </label>
              <select
                id="ai-dev-gpt-api-key"
                style={inputStyle}
                value={gptApiKeyId}
                onChange={(event) => this.setState({ gptApiKeyId: event.target.value })}
              >
                {gptApiKeyOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div style={{ marginTop: '8px', color: '#9fb0c3', fontSize: '0.92rem' }}>
                {selectedKey ? (
                  <span>
                    Model: <strong>{selectedKey.model || 'unknown'}</strong>{' '}
                    {selectedKey.active ? '(active)' : '(inactive)'}
                  </span>
                ) : (
                  'Select a GPT API key to expose it to the Ruby context.'
                )}
              </div>
            </div>

            <div>
              <label style={labelStyle} htmlFor="ai-dev-permission">
                Permission
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#c9d6e2' }}>
                <input
                  id="ai-dev-permission"
                  type="checkbox"
                  checked={permissionConfirmed}
                  onChange={(event) => this.setState({ permissionConfirmed: event.target.checked })}
                />
                I confirm I have permission to run arbitrary Ruby here.
              </label>
            </div>
          </div>
        </div>

        <div style={cardStyle}>
          <label style={labelStyle} htmlFor="ai-dev-code">
            Ruby
          </label>
          <textarea
            id="ai-dev-code"
            style={Object.assign({}, monoStyle, { minHeight: '360px', resize: 'vertical', lineHeight: 1.45 })}
            value={code}
            onChange={(event) => this.setState({ code: event.target.value })}
            spellCheck={false}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
            <div style={{ color: '#9fb0c3', fontSize: '0.92rem' }}>
              Runs inside the app process with `current_user` and `gpt_api_key` available in scope.
            </div>
            <button
              type="button"
              onClick={this.runCode}
              disabled={running || !permissionConfirmed || !gptApiKeyId || !code.trim()}
              style={{
                borderRadius: '10px',
                border: '1px solid #4b6a88',
                background: running ? '#2d3d4d' : '#1f3b55',
                color: '#fff',
                padding: '10px 16px',
                cursor: running ? 'wait' : 'pointer',
                fontWeight: 700
              }}
            >
              {running ? 'Running...' : 'Run Ruby'}
            </button>
          </div>
        </div>

        <div style={cardStyle}>
          <label style={labelStyle}>Output</label>
          {error && (
            <pre style={Object.assign({}, monoStyle, { whiteSpace: 'pre-wrap', color: '#ffb4b4' })}>{error}</pre>
          )}
          {result && (
            <div style={{ display: 'grid', gap: '12px' }}>
              <div style={{ color: '#9fb0c3' }}>
                Status: <strong>{result.ok ? 'ok' : 'error'}</strong> | Duration: <strong>{result.duration_ms}ms</strong>
              </div>
              <pre style={Object.assign({}, monoStyle, { whiteSpace: 'pre-wrap' })}>
                {JSON.stringify({
                  gpt_api_key: result.gpt_api_key,
                  result: result.result,
                  stdout: result.stdout,
                  stderr: result.stderr,
                  exception: result.exception
                }, null, 2)}
              </pre>
            </div>
          )}
          {!error && !result && (
            <div style={{ color: '#9fb0c3' }}>No output yet.</div>
          )}
        </div>
      </div>
    );
  }
}

